import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CacheKey } from '../../../shared/constants/cachekey.constants';
import { Country } from '../../../shared/interface/country.interface';
import { OrderInvoiceReportResponse } from '../../../shared/interface/order-invoice-reports.interface';
import { StoreConfig } from '../../../shared/interface/StoreConfig.interface';
import { UserInfo } from '../../../shared/interface/user-info.interface';
import { CacheService } from '../../../shared/services/cache.service';
import { CommonService } from '../../../shared/services/common.service';
import { Utils } from '../../../shared/Utils/utils';
import { DateConstants } from '../../constants/date.constants';
import { InvoiceColumn, InvoicePrintStatus, OrderStatusConstants, SortOrder } from '../../constants/reports.constants';
import { OperatingCountryEnums } from '../../enums/countries.enums';
import { NotificationType } from "../../enums/notification-types.enum";
import { PrintStatusEnum } from '../../enums/print-status.enum';
import { ReportTab } from '../../enums/report-tab.enum';
import { OperatingCountry } from '../../interface/operating-country.interface';
import { ParticipatingCountries } from '../../interface/participating-countries.interface';
import { AppConfigService } from '../../services/app-config.service';
import { CustomerService } from '../../services/customer.service';
import { OrderService } from '../../services/order.service';
import { ReportService } from '../../services/report.service';
import { AppCountryConfig } from '../../interface/app-config';
import { OrderStatus, ShippingType } from '../../enums/order-source-type';
import { Permission } from '../../../common/enums/permission.enum';
import { AccessLevel } from '../../../common/enums/access-level.enum';
import { StoreMetaDataConstants } from '../../constants/store-metadata.constants';
import { OrderSourceTypes } from '../../enums/order-source.enum';

@Component({
    selector: 'app-invoice-report',
    templateUrl: '../../templates/template1/views/reports/invoice-report.component.html',
    styleUrls: ['../../templates/template1/themes/default/less/invoice-report.component.less','../../templates/template1/themes/default/less/print-preview.less']
})
export class InvoiceReportComponent implements OnInit {

    operatingCountries: OperatingCountry[] = [];
    countries = [];
    stores: Country[] = [];
    businessEntities = [];
    userInfo: UserInfo;
    storeId: number;
    storeInfo: StoreConfig;
    operatingCountryCode: string;
    currentDate = new Date();
    fromDate: any;
    toDate: any;
    formattedFromDate: Date;
    formattedToDate: Date;
    isSubmitted: boolean = false;
    isRunLoading: boolean = false;
    isPrintLoading: boolean = false;
    dataLoadedCount = 0;
    momentConstructor: (value?: any) => moment.Moment = (<any>moment).default || moment;
    dateRangeDuration = 1;
    invoicePrintForm: FormGroup;
    isShowReport: boolean = false;
    pageSizes = ["50", "100", "150", "200", "All"];
    pageNumber: number = 1;
    pageSize: number = 50;
    selectedPageSize: any = this.pageSize;
    pageCount: number = 0;
    reportResponse:OrderInvoiceReportResponse[] = [];
    printCopies = [1, 2, 3, 4, 5];
    invoiceOptions = ["Invoice", "Packing Slip"];
    productCenters: any[] = [];
    printStatusConst = InvoicePrintStatus;
    printStatusEnum = PrintStatusEnum;
    selectedOrders: any[] =[];
    selectAll: boolean = false;
    orderInvoiceResponse: any[] = [];
    showPrint: boolean = false;
    storeMetadata: any;
    participatingCountries: ParticipatingCountries[] = [];
    pageQuantity: number = 2;
    showProductCenterPermissionErr = false;
    sortColumn: string = InvoiceColumn.ID;
    sortOrder: string = SortOrder.ASC;
    isDesc: boolean = false;
    invoiceColumnEnum = InvoiceColumn;
    sortOrderEnum = SortOrder;
    orderLogResponse=[];
    storeTimeZoneOffSet:any;
    appConfig: AppCountryConfig;
    defaultLoad = true;
    orderStatuses = [
        { id: OrderStatus.Completed, status: OrderStatusConstants.COMPLETED },
        { id: OrderStatus.Processing, status: OrderStatusConstants.PROCESSING },
        { id: OrderStatus.Ready_For_Pickup, status: OrderStatusConstants.READY_FOR_PICK_UP },
        { id: OrderStatus.Shipped, status: OrderStatusConstants.Shipped}       
    ];
    enableUpdateOrderCheckbox: boolean = false;
    hasServiceOrderUpdatePermissions: boolean = false;
    hasServiceOrderManagePermission: boolean = false;
    iosBrowserPrintStatus: boolean = true;

    constructor(
        private _customerService: CustomerService,
        private _cacheService: CacheService,
        private _formbuilder: FormBuilder,
        private _orderService: OrderService,
        private _reportService: ReportService,
        private _translateService: TranslateService,
        private _commonService: CommonService,
        private _appConfigService: AppConfigService
    ) {
    }

    ngOnInit() {
        this.userInfo = this._cacheService.get(CacheKey.UserInfo);

        this.getCountries();
        this.getOperatingCountries();
        this.buildInvoicePrintForm();
        this.toDate = this.momentConstructor(this.currentDate);
        this.hasServiceOrderUpdatePermissions = this._commonService.checkPermissionByAccessLevel(JSON.parse("[" + Permission.REPLACEMENT_ORDER_STATUS_UPDATE + "]"), AccessLevel.Customer_Service); 
        this.hasServiceOrderManagePermission = this._commonService.checkPermissionByAccessLevel(JSON.parse("[" + Permission.REPLACEMENT_ORDER_MANAGE + "]"), AccessLevel.Customer_Service);
        window.addEventListener('afterprint', () => {
            this.showPrint = false;
        });
    }

    /**
     * To build reports form
     */
    buildInvoicePrintForm(): void {
        this.invoicePrintForm = this._formbuilder.group({
            operatingCountry: ['', Validators.required],
            store: ['', [Validators.required]],
            productCenter: ['', [Validators.required]],
            printStatus: ['20', [Validators.required]],
            orderNumber: [''],
            fromDate: ['', [Validators.required, Utils.validateFromDate()]],
            toDate: ['', [Validators.required, Utils.validateToDate()]],
            printQuantity: [this.pageQuantity],
            printOption: ['', null],
            orderStatus:['',[Validators.required]],
            updateDeliveryStatus:[false],
            updatePickupStatus: [false]
        });
        this.setDefaultDateAndValidataors();
    }
    get printOption(){
        return this.invoicePrintForm && this.invoicePrintForm.controls['printOption'].value;
    }

    /**
     * To change page size
     * @param  {number} pageSize
     * @returns void
     */
    pageSizeChange(size: any): void {
        this.pageNumber = 1;
        this.selectedPageSize = size;
        this.pageSize = size === "All" ? null : +size;
        this.onSubmitRunReport();
    }

    /**
     * To change page number
     * @param  {number} pageNumber
     */
    pageChange(pageNumber: number): void {
        if (pageNumber) {
            this.pageNumber = pageNumber;
            this.onSubmitRunReport();
        }
    }

    /**
     * To get all operating countries from configuration service
     */
    getOperatingCountries() {
        this._customerService.getAllBusinessEntities().subscribe((response: any) => {
            if (response) {
                this.businessEntities = response;
                this.getAllowedOperatingCountries();
            }
        }, (error) => {
            this.businessEntities = [];
        });
    }

    /**
     * To get allowed Countries based on permissions
     */
    getAllowedOperatingCountries() {
        let countryIds: any = [];
        if (this.userInfo && !this.userInfo.hasSystemRole) {
            if (this.userInfo.allowedOperatingCountries) {
                countryIds = this.userInfo.allowedOperatingCountries.split(',');
                countryIds = countryIds ? countryIds : [];
                if (countryIds && countryIds.length > 0) {
                    let ids = countryIds;
                    this.operatingCountries = this.businessEntities.filter(function (item) {
                        return ids.indexOf(item.id + '') !== -1;
                    });
                }
            } else {
                this.operatingCountries = [];
            }
        } else {
            this.operatingCountries = this.businessEntities;
        }
        this.operatingCountries.sort(function (item1, item2) {
            const country1 = item1.name.toLowerCase(), country2 = item2.name.toLowerCase();
            if (country1 < country2) {
                return -1;
            }
            if (country1 > country2) {
                return 1;
            }
            return 0;
        });
        const countryInfo = this.operatingCountries.find((country: any) => country.id === OperatingCountryEnums.USA);
        if (countryInfo) {
            // this.operatingCountries.splice(this.countries.indexOf(countryInfo), 1);
            // this.operatingCountries.splice(0, 0, countryInfo);
            this.invoicePrintForm.controls['operatingCountry'].setValue(countryInfo.id);
            this.getStoresByCountryCode(countryInfo.id,countryInfo.value);
            this.defaultLoad = false;
        }
        if (this.countries && this.countries.length > 0 && !countryInfo && !this.defaultLoad) {
            this.getStoresByCountryCode(this.operatingCountries[0].id, this.operatingCountries[0].value);
        }
    }

    /**
     * To get all countries from configuration service
     */
    getCountries() {
        this._customerService.getAllCountries(true).subscribe(response => {
            if (response) {
                this.countries = response;
                if (this.operatingCountries && this.operatingCountries.length > 0) {
                    this.getStoresByCountryCode(this.operatingCountries[0].id, this.operatingCountries[0].value);
                }
            }
        }, (error) => {
            this.countries = [];
        });
    }

    /**
     * To get operating country by country code
     */
    getStoresByCountryCode(operatingCountryId: number, isoCodeThree?: string) {
        this.operatingCountryCode = isoCodeThree ? isoCodeThree : this.operatingCountries.find(operatingCountry => operatingCountry.id === +operatingCountryId).value;
        this._customerService.getOperatingCountriesByCountryCode(this.operatingCountryCode, true).subscribe(response => {
            if (response && response.countriesUnderOperatingCountry && response.countriesUnderOperatingCountry.length > 0) {
                this.stores = [];
                response.countriesUnderOperatingCountry.forEach(countryOperatingCountry => {
                    const countries = this.countries.filter(country => country.titanPlatformEnabled && country.enrollmentEnabled && country.shopEnabled
                        && country.isocodeThree === countryOperatingCountry.countryCode);
                    if (countries && countries.length > 0) {
                        this.stores = this.stores.concat(countries);
                    }
                });
                if(this.stores && this.stores.length === 1){
                    const defaultStore = this.stores[0];
                    this.invoicePrintForm.patchValue({store:defaultStore.countryId});
                    this.onStoreChange(defaultStore.countryId);
                }
            } else {
                this.stores = [];
            }
        }, (error) => {
            this.stores = [];
        });
    }

    /**
     * On operating country change event
     */
    onOperatingCountryChange(operatingCountryId: number): void {
        this.invoicePrintForm.controls['printOption'].setValue('');
        this.orderNumberFormValueReset('store','');
        this.orderNumberFormValueReset('productCenter','');
        this.orderNumberFormValueReset('orderNumber','');
        this.orderNumberFormValueReset('updatePickupStatus',false);
        this.orderNumberFormValueReset('updateDeliveryStatus',false);
        this.isShowReport = false;
        this.storeId = null;
        this.storeInfo = null;
        this.storeMetadata = null;
        this.productCenters = [];
        this.participatingCountries = [];
        this.selectedOrders=[];
        this.showProductCenterPermissionErr = false;
        this.getStoresByCountryCode(operatingCountryId);
    }

    /**
     * On store change event
     */
    onStoreChange(id: number): void {
        this.isShowReport = false;
        this.orderNumberFormValueReset('productCenter','');
        this.orderNumberFormValueReset('orderNumber','');
        this.orderNumberFormValueReset('updatePickupStatus',false);
        this.orderNumberFormValueReset('updateDeliveryStatus',false);
        this.storeId = null;
        this.storeInfo = null;
        this.storeMetadata = null;
        this.productCenters = [];
        this.participatingCountries = [];
        this.selectedOrders=[];
        this.showProductCenterPermissionErr = false;
        const countryCode = this.stores.filter(country => country.countryId === Number(id))[0].isocodeThree;
        this.appConfig = this._appConfigService.getSettings(countryCode, null);
        this.getStoreInfo(countryCode);
    }

    /**
     * To get store info by countryCode
     */
    getStoreInfo(countryCode: string) {
        this._orderService.getStoreInfo(countryCode).subscribe(response => {
            if (response) {
                this.storeInfo = response;
                 if(this.storeInfo.timeZoneOffset.toString().startsWith("-")){
                    this.storeTimeZoneOffSet = this.storeInfo.timeZoneOffset.toString();
                }
                else{
                    this.storeTimeZoneOffSet = '+' + this.storeInfo.timeZoneOffset;
                }
                this.storeId = response.id;
                this.getStoreMetaData(this.storeId);
                this.getProductCenterByStoreId(this.storeId);
                this.getParticipatingCountries(countryCode);
            } else {
                this.storeInfo = null;
            }
        }, (error) => {
            this.storeInfo = null;
        });
    }

    /**
     * Get store metadata
     * @param storeId
     */
    getStoreMetaData(storeId: any) {
        this._commonService.getStoreMetaData(storeId).subscribe(response => {
            if (response) {
                this.storeMetadata = response;
                this.enableUpdateOrderCheckbox = ((this.getStoreMetaDataPermissions() && this.getStorePermissionsForUpdateOrders()) || this.hasServiceOrderManagePermission);
            } else {
                this.storeMetadata = null;
                this.enableUpdateOrderCheckbox = false
            }
        }, (error) => {
            this.storeMetadata = null;
            this.enableUpdateOrderCheckbox = false
        });
    }

    /**
     * Get participating countries
     * @param storeId
     */
    getParticipatingCountries(countryCode: any) {
        this._customerService.getParticipatingCountries(countryCode).subscribe((response) => {
            if (response) {
                this.participatingCountries = response;
            } else {
                this.participatingCountries = [];
            }
        }, (error) => {
            this.participatingCountries = [];
        });
    }

    /**
     * To get product centers by storeId
     */
    getProductCenterByStoreId(storeId: number) {
        this._customerService.getProductCenter(storeId).subscribe(response => {
            if (response && response.length > 0) {
                this.getAllowedProductCenters(response);
            } else {
                this.productCenters = [];
            }
        }, (error) => {
            this.productCenters = [];
        });
    }
    /**
     * Get agent allowed product centers
     * @param response
     */
    getAllowedProductCenters(response: any[]) {
        let productCenterIds=[];
        this.productCenters = [];
        if (this.userInfo && !this.userInfo.hasSystemRole) {
            if (this.userInfo.allowedProductCenters.length) {
                const allowedProductCenters = this.userInfo.allowedProductCenters.filter((item) => item.storeId == this.storeId);
                if(allowedProductCenters && allowedProductCenters.length > 0) {
                    productCenterIds = productCenterIds.concat(allowedProductCenters[0].productCenters.split(','));
                    this.productCenters = response.filter((x) => productCenterIds.indexOf(x.id.toString()) > -1);
                    this.showProductCenterPermissionErr=false;
                } else {
                    this.showProductCenterPermissionErr=true;
                }
            } else {
                this.showProductCenterPermissionErr=true;
            }
        } else {
            this.showProductCenterPermissionErr=false;
            this.productCenters = response;
        }
        if(this.productCenters && this.productCenters.length === 1){
            const defaultProdcutCenter = this.productCenters[0];
            this.invoicePrintForm.patchValue({productCenter:defaultProdcutCenter.name});
            this.onChangeProductCenter();
        }
    }

    /**
     * Set Default Date & Validators for Ordres Report
     */
    setDefaultDateAndValidataors(): void {
        let toDate = new Date();
        let fromDate = new Date();
        fromDate.setDate(toDate.getDate() - 3);
        this.fromDate=this.momentConstructor(new Date(fromDate));
        this.invoicePrintForm.get('printStatus').setValue("20");
        this.invoicePrintForm.controls['fromDate'].setValue(this.momentConstructor(new Date(fromDate)));
        this.invoicePrintForm.controls['toDate'].setValue(this.momentConstructor(this.toDate));
        this.invoicePrintForm.controls['fromDate'].setValidators([Validators.required, Utils.validateFromDate(1, 'M')]);
        this.invoicePrintForm.controls['toDate'].setValidators([Validators.required, Utils.validateToDate(1, 'M')]);
        this.invoicePrintForm.controls['fromDate'].updateValueAndValidity();
        this.invoicePrintForm.controls['toDate'].updateValueAndValidity();
        this.dateRangeDuration = 1;
    }

    /**
     * When form values are changed
     */
    formValuesChanged(): void {
        this.isSubmitted = false;
        this.dataLoadedCount = 0;
    }


    /**
     * Update the date range validation for ToDate when change from Date
     */
    fromDateChange(): void {
        this.formValuesChanged();
        setTimeout(() => {
            if (!this.invoicePrintForm.controls['toDate'].valid
                && (this.invoicePrintForm.controls['toDate'].hasError('monthRangeValidation')
                    || this.invoicePrintForm.controls['toDate'].hasError('toDateValidation'))) {
                this.invoicePrintForm.controls['toDate'].setValidators([Validators.required, Utils.validateToDate(this.dateRangeDuration, 'M')]);
                this.invoicePrintForm.controls['toDate'].updateValueAndValidity();
            } else if (!this.invoicePrintForm.controls['toDate'].valid
                && this.invoicePrintForm.controls['toDate'].hasError('dayRangeValidation')
                || this.invoicePrintForm.controls['toDate'].hasError('toDateValidation')) {
                this.invoicePrintForm.controls['toDate'].setValidators([Validators.required, Utils.validateToDate(1, 'M')]);
                this.invoicePrintForm.controls['toDate'].updateValueAndValidity();
            }
        }, 0);
    }

    /**
     * Update the date range validation for fromDate when change To Date
     */
    toDateChange(): void {
        this.formValuesChanged();
        setTimeout(() => {
            if (!this.invoicePrintForm.controls['fromDate'].valid
                && (this.invoicePrintForm.controls['fromDate'].hasError('monthRangeValidation')
                    || this.invoicePrintForm.controls['fromDate'].hasError('fromDateValidation'))) {
                this.invoicePrintForm.controls['fromDate'].setValidators([Validators.required, Utils.validateFromDate(this.dateRangeDuration, 'M')]);
                this.invoicePrintForm.controls['fromDate'].updateValueAndValidity();
            } else if (!this.invoicePrintForm.controls['fromDate'].valid
                && (this.invoicePrintForm.controls['fromDate'].hasError('dayRangeValidation')
                    || this.invoicePrintForm.controls['fromDate'].hasError('fromDateValidation'))) {
                this.invoicePrintForm.controls['fromDate'].setValidators([Validators.required, Utils.validateFromDate(1, 'M')]);
                this.invoicePrintForm.controls['fromDate'].updateValueAndValidity();
            }
        }, 0);
    }

    /**
     * on click run report
     */
    onSubmitRunReport(sortByColumn?: string, sortOrder?: string) {
        this.orderLogResponse=[];
        this.selectAll =false;
        this.selectedOrders=[];
        this.formattedFromDate = Utils.convertToSpecificDateFormat(moment(this.invoicePrintForm.controls['fromDate'].value), DateConstants.DOBFormat);
        this.formattedToDate = Utils.convertToSpecificDateFormat(moment(this.invoicePrintForm.controls['toDate'].value), DateConstants.DOBFormat);
        this.isRunLoading = true;
        this.sortColumn = sortByColumn ? sortByColumn : this.sortColumn;
        this.sortOrder = sortOrder ? sortOrder : this.isDesc ? this.sortOrderEnum.DESC : this.sortOrderEnum.ASC;
        const statusId = [];
        this.pageSize  = this.selectedPageSize === 'All' ? null : this.pageSize;
        this.invoicePrintForm.controls['printOption'].setValue('');
        this.invoicePrintForm.controls['updatePickupStatus'].setValue(false);
        this.invoicePrintForm.controls['updateDeliveryStatus'].setValue(false);
        if(this.invoicePrintForm.controls['printStatus'].value == 'All') {
            statusId.push(this.printStatusEnum.Not_Printed);
            statusId.push(this.printStatusEnum.Printed);
        } else {
            statusId.push(this.invoicePrintForm.controls['printStatus'].value);
        }
        const orderStatusId = this.selectedOrderStatus();
        this._reportService.getOrderInvoiceReport(this.storeId, this.invoicePrintForm.controls['productCenter'].value, statusId,
            this.formattedFromDate, this.formattedToDate, this.pageSize, this.pageNumber, this.sortColumn, this.sortOrder, this.invoicePrintForm.controls['orderNumber'].value,orderStatusId).subscribe(response => {
                if (response && response.content && response.content.length > 0) {
                    this.reportResponse = response.content;
                    this.reportResponse.forEach(data=>{
                        if(data.storePostedDate){
                            data.storePostedDate = moment.utc(data.storePostedDate).format('DD-MMM-YYYY HH:mm:ss')
                        }
                    });
                    this.isRunLoading = false;
                    this.pageSize = this.pageSize ? this.pageSize : response.totalCount;
                    this.pageCount = response.totalCount;
                    this.isShowReport=true;
                } else {
                    const message: any = this._translateService.get('reports.Orders not found for selected filter criteria');
                    this._commonService.createNotification('', message.value, NotificationType.ERROR);
                    this.reportResponse = [];
                    this.isRunLoading = false;
                    this.pageCount = 0;
                    this.isShowReport=false;
                    this.selectedOrders = [];
                    this.selectAll = false;
                }
            }, (error) => {
                const message: any = this._translateService.get('reports.Something Went Wrong, Please try again after some time');
                this._commonService.createNotification('', message.value, NotificationType.ERROR);
                this.reportResponse = [];
                this.isRunLoading = false;
                this.pageCount = 0;
                this.isShowReport=false;
                this.selectedOrders = [];
                this.selectAll = false;
            });
    }

    selectedOrderStatus() {
        if (this.invoicePrintForm.get('orderStatus').value == "All") return this.orderStatuses.map(status => status.id)
        return [+this.invoicePrintForm.get('orderStatus').value];
    }

    /**
     * on click print report
     */
    onSubmitPrintReport() {
        this.isPrintLoading = true;
        this.updateOrderStatues();
        this._reportService.getOrderInvoicePrintReport(this.storeId, this.selectedOrders).subscribe(response => {
            if (response && response.length > 0) {
                this.orderInvoiceResponse = response;
                this.isPrintLoading = false;
                this.showPrint=true;
                setTimeout(() => {
                        window.print();
                        this.iosBrowserPrintStatus = false;
                },0);

                this.updateOrderInvoicePrintStatus();
            } else {
                this.orderInvoiceResponse = [];
                this.isPrintLoading = false;
                this.showPrint=false;
            }
        }, (error) => {
            const message: any = this._translateService.get('reports.Something Went Wrong, Please try again after some time');
            this._commonService.createNotification('', message.value, NotificationType.ERROR);
            this.orderInvoiceResponse = [];
            this.isPrintLoading = false;
            this.showPrint=false;
        });
    }
    onClickWebOrderNnmber(webOrderId:string){
        // this.searchService.log.startDate;
        this.orderLogResponse=[];
        let startDate = new Date(this.formattedFromDate);
        let endDate = new Date(this.formattedToDate);
        startDate = new Date(startDate.getTime() - (startDate.getTimezoneOffset()*60*1000));
        endDate = new Date(endDate.getTime() - (endDate.getTimezoneOffset()*60*1000));
        endDate.setHours(23);
        endDate.setMinutes(59);
        endDate.setSeconds(59);
        const request={
            "messageType": 0,
            "refId": webOrderId,
            "startDate": startDate,
            "endDate": endDate,
            "pageNumber": 1,
            "pageSize": 10,
            "export": false
        }
        this._reportService.getOrderLogsFromDI(request,true,null).subscribe(response=>{
            if (response  && response.requestLookUpDetails && response.requestLookUpDetails.length>0) {
                this.orderLogResponse=response.requestLookUpDetails;
            }

        });
    }
    /**
     * Update Order invoice print status
     */
    updateOrderInvoicePrintStatus() {
        const userName = ((this.userInfo.firstName ? this.userInfo.firstName :"")
            + (this.userInfo.middleName ? " " + this.userInfo.middleName : "")
            + (this.userInfo.lastName ? " " + this.userInfo.lastName : "")).replace("  ", " ").trim();
        this._reportService.updateOrderInvoicePrintStatus(this.selectedOrders, this.userInfo.userId, userName).subscribe(response => {
            if (response && response.status) {
                this.onSubmitRunReport();
            } else {
                const message: any = this._translateService.get('reports.Error saving invoice print status');
                this._commonService.createNotification('', message.value, NotificationType.ERROR);
            }
        }, (error) => {
            const message: any = this._translateService.get('reports.Error saving invoice print status');
            this._commonService.createNotification('', message.value, NotificationType.ERROR);
        });
    }

    /**
     * On print quantity change event
     */
    onPrintQuantityChange(quantity: number) {
        this.pageQuantity = quantity;
    }

    /**
     * On check box click event
     */
    onOrderSelect(event: any, orderInvoice?: any) {
        if (event.target.checked) {
            if(orderInvoice) {
                if (!orderInvoice.isSelected) {
                    orderInvoice.isSelected = true;
                    this.selectedOrders.push(orderInvoice.id);
                }
            } else {
                if (this.reportResponse.length) {
                    for (const report of this.reportResponse) {
                        if (!report.isSelected) {
                            report.isSelected = true;
                            this.selectedOrders.push(report.id);
                        }
                    }
                    this.selectAll = this.reportResponse.every(report => report.isSelected === true);
                }
            }
            this.selectAll = this.reportResponse.every(report => report.isSelected === true);
        } else {
            if(orderInvoice) {
                orderInvoice.isSelected = false;
                this.selectedOrders.splice(this.selectedOrders.indexOf(orderInvoice.id), 1);
            } else {
                if (this.reportResponse.length) {
                    for (const report of this.reportResponse) {
                        report.isSelected = false;
                        this.selectedOrders.splice(this.selectedOrders.indexOf(report.id), 1);
                    }
                    this.selectAll = this.reportResponse.every(report => report.isSelected === true);
                }
            }
            this.selectAll = false
        }
    }

    /**
     * track by index
     * @param index
     * @returns
     */
    trackByItem(index: number): number {
        return index;
    }

    /**
     * On change event for product center
     */
    onChangeProductCenter() {
        this.selectedOrders=[];
        this.isShowReport=false;
    }

    /**
     * Sort by column name
     * @param sortColumn
     */
    sortByColumn(sortColumn: string) {
        this.isDesc = (this.sortColumn == sortColumn || sortColumn == "") ? !this.isDesc : this.isDesc;
        this.sortColumn = sortColumn;
        this.sortOrder = this.isDesc ? this.sortOrderEnum.DESC : this.sortOrderEnum.ASC;
        this.onSubmitRunReport();
    }
    /**
     * To reset order number form value
     */
    orderNumberFormValueReset(control, value) {
        this.invoicePrintForm.get(control).setValue(value);
        this.invoicePrintForm.get(control).markAsPristine();
        this.invoicePrintForm.get(control).markAsUntouched();
    }

    populatePickupOrders(isPickUp: any) {
        let webOrdeIDS = [];
        const maxLimitSelection = 25;
        if (this.selectedOrders && !!this.selectedOrders.length  && this.reportResponse && !!this.reportResponse.length) {
            for (let i = 0; i <= this.selectedOrders.length-1; i++) {
                const orders = this.reportResponse.find(orders=>orders.id==this.selectedOrders[i]);
                if (orders && orders.orderStatusId == OrderStatus.Processing && 
                    orders.webOrderId && orders.deliveryPreferenceId == ShippingType.PICKUP && 
                    orders.orderSource && orders.orderSource.toLowerCase() != OrderSourceTypes.POS.toLowerCase() && isPickUp) {
                    webOrdeIDS.push(orders.webOrderId);
                }
                else if(orders && orders.orderStatusId == OrderStatus.Processing && 
                    orders.webOrderId && orders.deliveryPreferenceId == ShippingType.DELIVERY && 
                    orders.orderSource && orders.orderSource.toLowerCase() != OrderSourceTypes.POS.toLowerCase() && !isPickUp){
                    webOrdeIDS.push(orders.webOrderId);   
                    }
                if (webOrdeIDS.length == maxLimitSelection) break;
            }
        }
        return webOrdeIDS;
    }

    getPermissionsForUpdateOrders() {
        let hasServiceOrderUpdatePermissions = this._commonService.checkPermissionByAccessLevel(JSON.parse("[" + Permission.REPLACEMENT_ORDER_STATUS_UPDATE + "]"), AccessLevel.Customer_Service);
        let hasServiceOrderManagePermission = this._commonService.checkPermissionByAccessLevel(JSON.parse("[" + Permission.REPLACEMENT_ORDER_MANAGE + "]"), AccessLevel.Customer_Service);
        return (hasServiceOrderUpdatePermissions || hasServiceOrderManagePermission);
    }

    getStoreMetaDataPermissions() {
        let orderStatusUpdateAttribute = this.storeMetadata && !!this.storeMetadata.length && this.storeMetadata.filter(x => (x.attribute &&
            (x.attribute.toLowerCase() === (StoreMetaDataConstants.ENABLE_ORDER_STATUS_UPDATE.toLowerCase() || StoreMetaDataConstants.ENABLE_SERVICE_ORDER.toLowerCase()))));
        return orderStatusUpdateAttribute && !!orderStatusUpdateAttribute.length && orderStatusUpdateAttribute.some(attribute => attribute.metaDataValue && attribute.metaDataValue.toLowerCase() === 'true');
    }
    getStorePermissionsForUpdateOrders() {
        let orderStatusUpadte = this._commonService.checkPermissionByAccessLevel(JSON.parse("[" + Permission.ORDER_STATUS_UPDATE + "]"), AccessLevel.Store, this.storeInfo ? this.storeInfo.id : null)
        return (this.hasServiceOrderUpdatePermissions || orderStatusUpadte);
    }

    updateOrderStatues() {
        const orderStatus = this.invoicePrintForm.get('orderStatus').value;
        const updateStatus = this.invoicePrintForm.get('updatePickupStatus').value;
        const updateDeliveryStatus = this.invoicePrintForm.get('updateDeliveryStatus').value;
        if (this.enableUpdateOrderCheckbox && updateStatus && orderStatus && (orderStatus == "All" || orderStatus == OrderStatus.Processing)) {
            const orderIds = this.populatePickupOrders(true);
            if (orderIds && !!orderIds.length) {
                this._reportService.updateProcessingOrderStatus(orderIds, (this.userInfo && this.userInfo.userId))
                    .subscribe(res => {
                        if(res && res.success){
                            const message: any = this._translateService.get('reports.Order is Processing in Background, Please check after some time');
                            this._commonService.createNotification('', message.value, NotificationType.SUCCESS);
                        }else{
                            const message: any = this._translateService.get('reports.Something Went Wrong While updating order statuses, Please try again after some time');
                            this._commonService.createNotification('', message.value, NotificationType.ERROR);
                        }
                        
                    }, (err) => {
                        const message: any = this._translateService.get('reports.Something Went Wrong While updating order statuses, Please try again after some time');
                        this._commonService.createNotification('', message.value, NotificationType.ERROR);
                    })
            }
            ;
        } 
        if (this.enableUpdateOrderCheckbox && updateDeliveryStatus && orderStatus && (orderStatus == "All" || orderStatus == OrderStatus.Processing)) {
            const orderIds = this.populatePickupOrders(false);
            if (orderIds && !!orderIds.length) {
                let request = {
                    "webOrderIds": orderIds
                }
                this._reportService.updateDeliveryProcessingOrderStatus(request).subscribe(res => {
                    if (res && res.success) {
                        const message: any = this._translateService.get('reports.Order is Processing in Background, Please check after some time');
                        this._commonService.createNotification('', message.value, NotificationType.SUCCESS);
                    } else {
                        const message: any = this._translateService.get('reports.Something Went Wrong While updating order statuses, Please try again after some time');
                        this._commonService.createNotification('', message.value, NotificationType.ERROR);
                    }
                }, err => {
                    const message: any = this._translateService.get('reports.Something Went Wrong While updating order statuses, Please try again after some time');
                    this._commonService.createNotification('', message.value, NotificationType.ERROR);
                })
            }
        }
    }
    orderStatusChange() {
        const orderStatus = this.invoicePrintForm.get('orderStatus').value;
        if (orderStatus == "All" || orderStatus == OrderStatus.Processing) {
            this.invoicePrintForm.get('updatePickupStatus').enable();
            this.invoicePrintForm.get('updateDeliveryStatus').enable();
        }
        else {
            this.invoicePrintForm.get('updatePickupStatus').setValue(false);
            this.invoicePrintForm.get('updatePickupStatus').disable();
            this.invoicePrintForm.get('updateDeliveryStatus').setValue(false);
            this.invoicePrintForm.get('updateDeliveryStatus').disable();
        }
        this.invoicePrintForm.get('updatePickupStatus').updateValueAndValidity();
        this.invoicePrintForm.get('updateDeliveryStatus').updateValueAndValidity();
    }

    onFocusOut(){
        this.showPrint = false;
    }

  
}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/reports/invoice-report.component.ts